import React, { useEffect, useState } from 'react';
import { Card } from '../../components/design-system/Card';
import { Button } from '../../components/design-system/Button';
import { useAuth } from '../../hooks/useAuth';
import { clsx } from 'clsx';

interface TodaysFocus {
  exercise: string;
  duration: number;
  description: string;
  icon: string;
}

export const BeginnerDashboard: React.FC = () => {
  const { user, isGuest } = useAuth();
  const [timeOfDay, setTimeOfDay] = useState<'morning' | 'afternoon' | 'evening'>('morning');
  const [streak, setStreak] = useState(0); // Start at 0 for guests
  const [todaysFocus] = useState<TodaysFocus>({
    exercise: 'Try a Workout',
    duration: 5,
    description: 'Explore our features',
    icon: '💪'
  });

  // Determine time of day for personalized greeting
  useEffect(() => {
    const hour = new Date().getHours();
    if (hour < 12) setTimeOfDay('morning');
    else if (hour < 18) setTimeOfDay('afternoon');
    else setTimeOfDay('evening');
  }, []);

  const greetings = {
    morning: 'Good morning',
    afternoon: 'Good afternoon',
    evening: 'Good evening'
  };

  const dailyWisdom = [
    'Consistency beats intensity',
    'Every rep counts',
    'Progress, not perfection',
    'Small steps, big results',
    'Your only competition is yesterday'
  ];

  const getTodaysWisdom = () => {
    const dayOfYear = Math.floor((Date.now() - new Date(new Date().getFullYear(), 0, 0).getTime()) / 86400000);
    return dailyWisdom[dayOfYear % dailyWisdom.length];
  };

  const handleStartWorkout = () => {
    // Navigate to workout or trigger workout flow
    console.log('Starting workout...');
    
    // Trigger haptic feedback if available
    if ('vibrate' in navigator) {
      navigator.vibrate([50, 30, 100]); // Success pattern
    }
  };

  // Generate streak display
  const renderStreak = () => {
    return Array.from({ length: Math.min(streak, 7) }, (_, i) => (
      <span key={i} className="text-2xl animate-pulse" style={{ animationDelay: `${i * 100}ms` }}>
        🔥
      </span>
    ));
  };

  // Guest-specific content
  if (isGuest) {
    return (
      <div className="min-h-screen bg-gray-50 px-4 py-8 animate-fadeIn">
        <div className="max-w-md mx-auto space-y-6">
          {/* Guest Welcome */}
          <header className="text-center space-y-1">
            <h1 className="text-2xl font-semibold text-gray-900">
              Welcome to FitGenie! 👋
            </h1>
            <p className="text-gray-600">Explore what we have to offer</p>
          </header>

          {/* Feature Cards */}
          <Card variant="highlighted" padding="lg" className="text-center space-y-4">
            <h2 className="text-xl font-semibold text-gray-900">
              AI-Powered Fitness Plans
            </h2>
            <p className="text-gray-600">
              Get personalized workout plans based on your goals and fitness level
            </p>
            <ul className="text-left space-y-2 mx-auto max-w-xs">
              <li className="flex items-center gap-2">
                <span className="text-green-500">✓</span>
                <span>Beginner-friendly workouts</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="text-green-500">✓</span>
                <span>Progress tracking</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="text-green-500">✓</span>
                <span>Community support</span>
              </li>
            </ul>
          </Card>

          {/* Try a Sample Workout */}
          <Card variant="default" padding="md">
            <h3 className="font-semibold text-gray-900 mb-2">Try a Sample Workout</h3>
            <p className="text-gray-600 mb-4">
              Experience our 5-minute beginner routine
            </p>
            <Button 
              variant="secondary" 
              fullWidth
              onClick={() => alert('Sign up to access workouts!')}
            >
              Preview Workout
            </Button>
          </Card>

          {/* CTA */}
          <Card variant="primary" padding="lg" className="text-center text-white">
            <h3 className="text-xl font-semibold mb-2">
              Ready to Start Your Journey?
            </h3>
            <p className="mb-4 opacity-90">
              Join thousands achieving their fitness goals
            </p>
            <Button 
              variant="secondary" 
              size="lg" 
              fullWidth
              onClick={() => window.location.href = '/register'}
              className="bg-white text-[#4B5FDC] hover:bg-gray-100"
            >
              Sign Up Now →
            </Button>
          </Card>

          <footer className="text-center pt-4">
            <p className="text-sm text-gray-500">
              Already have an account?{' '}
              <a href="/login" className="text-[#4B5FDC] hover:underline">
                Sign in
              </a>
            </p>
          </footer>
        </div>
      </div>
    );
  }

  // Regular user content
  return (
    <div className="min-h-screen bg-gray-50 px-4 py-8 animate-fadeIn">
      <div className="max-w-md mx-auto space-y-6">
        {/* Personalized Greeting */}
        <header className="text-center space-y-1">
          <h1 className="text-2xl font-semibold text-gray-900">
            {greetings[timeOfDay]}, {user?.first_name || 'there'}! 
            {timeOfDay === 'morning' && ' ☀️'}
            {timeOfDay === 'afternoon' && ' 🌤️'}
            {timeOfDay === 'evening' && ' 🌙'}
          </h1>
          <p className="text-gray-600">Ready to move?</p>
        </header>

        {/* Today's Focus - Primary Card */}
        <Card
          variant="highlighted"
          padding="lg"
          className="text-center space-y-6 animate-slideUp"
          style={{ animationDelay: '100ms' }}
        >
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Today's Focus: {todaysFocus.exercise} {todaysFocus.icon}
            </h2>
            <div className="flex items-center justify-center gap-6 mt-4">
              <div className="bg-[#4B5FDC]/10 rounded-xl px-6 py-4">
                <p className="text-3xl font-bold text-[#4B5FDC]">{todaysFocus.duration}min</p>
              </div>
              <div className="text-left">
                <p className="text-lg text-gray-700">{todaysFocus.description}</p>
              </div>
            </div>
          </div>

          <Button
            size="lg"
            variant="primary"
            onClick={handleStartWorkout}
            haptic
            celebrateOnClick
            fullWidth
            className="text-lg font-semibold"
          >
            Start Now →
          </Button>
        </Card>

        {/* Streak Display */}
        <div 
          className="text-center space-y-2 animate-slideUp"
          style={{ animationDelay: '200ms' }}
        >
          <p className="text-gray-600">Your streak:</p>
          <div className="flex justify-center gap-1">
            {renderStreak()}
          </div>
          <p className="text-sm text-gray-500">({streak} days)</p>
        </div>

        {/* Daily Wisdom */}
        <Card
          variant="subtle"
          padding="md"
          className="text-center animate-slideUp"
          style={{ animationDelay: '300ms' }}
        >
          <p className="text-lg text-gray-700">
            <span className="text-2xl mr-2">💡</span>
            "{getTodaysWisdom()}"
          </p>
        </Card>

        {/* Minimal Footer */}
        <footer className="text-center pt-8">
          <p className="text-sm text-gray-400">
            One step at a time
          </p>
        </footer>
      </div>
    </div>
  );
};

// Mobile-optimized styles
const mobileStyles = `
  @media (max-width: 640px) {
    .dashboard-container {
      padding: 1rem;
    }
  }
  
  /* Ensure touch targets are large enough */
  button {
    min-height: 48px;
  }
  
  /* Smooth animations */
  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

export default BeginnerDashboard;